import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import { CUSTOMER_ANONYMOUS } from '@/configs/reserved-word';
import { usePublic } from '@/contexts/PublicContext';
import { retrieveHeySynclyChatFeedbacks } from '@/entities/hey-syncly';
import { FeedbackExport } from '@/features/Feedback/components/FeedbackExport/FeedbackExport';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { useFeedbackSortState } from '@/features/Feedback/hooks/useFeedbackSortState';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services';
import type { DetailModalOptions } from '@/stores/pure/DetailModalStore';
import { detailModalStyle } from '@/styles/style';
import { getFeedbackSourceName, SOURCE_TYPE } from '@/types/feedbackConst';

import { MentionItem } from './mention-item';
import type { FlattenMentionItem, MentionListModalOptions } from './types';

type Props = {
  alias: string;
  pos: number;
  options: DetailModalOptions<MentionListModalOptions>;
  closeModal: () => void;
  onSelectNewModal: (alias: string, blockAlias?: string) => void;
};

export const MentionListModal = (props: Props) => {
  const isPublic = usePublic();
  const scrollRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);

  const { data, isLoading } = useQuery({
    queryKey: [
      props.alias,
      ...props.options.citation_ids,
      sortStatus?.sortBy,
      sortStatus?.orderStatus,
    ],
    queryFn: async () => {
      if (isPublic) {
        // Public일 때에는 report_alias 필수
        if (!props.options.report_alias)
          throw new Error('report_alias is required');
        return retrieveHeySynclyChatFeedbacks(
          {
            isPublic: true,
            chat_alias: props.options.chat_alias,
            message_id: props.options.message_id,
            report_alias: props.options.report_alias,
            citation_ids: props.options.citation_ids,
          },
          sortStatus,
        );
      }
      return retrieveHeySynclyChatFeedbacks(
        {
          isPublic: false,
          chat_alias: props.options.chat_alias,
          message_id: props.options.message_id,
          citation_ids: props.options.citation_ids,
        },
        sortStatus,
      );
    },
  });

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    const openFeedbackModal = () => {
      setSelectedFeedback(state);
      props.onSelectNewModal(state.feedbackAlias, state.blockAlias);
    };
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        openFeedbackModal();
      }
    } else {
      openFeedbackModal();
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.alias]);

  useTrackModal({
    domain: 'hey-syncly',
    alias: props.alias,
    options: {
      citation_ids: props.options.citation_ids,
      chat_alias: props.options.chat_alias,
      message_id: props.options.message_id,
    },
  });

  const onCloseClick = () => {
    props.closeModal();
  };

  useKeymap('Escape', onCloseClick);

  const flattenMentionItems: FlattenMentionItem[] = useMemo(() => {
    return (
      data?.feedbacks?.flatMap(
        (f) =>
          f.blocks?.map((b) => {
            const mention: FlattenMentionItem = {
              feedback_alias: f.alias,
              alias: b.alias,
              text: b.snippet,
              contact_name: f.customer_info?.name || CUSTOMER_ANONYMOUS,
              account_name: f.company_info?.name || '',
              date: f.date,
              source_type: f.source?.type || SOURCE_TYPE.MANUAL,
              source_name: f.source?.type
                ? getFeedbackSourceName(f.source?.type)
                : '',
              source_domain: f.source?.domain || '',
              source_channel: f.source?.channel || '',
              remarks: f.remarks ? f.remarks.join(',') : '',
              collect_count: f.engagement?.collect_count,
              comment_count: f.engagement?.comment_count,
              like_count: f.engagement?.like_count,
              share_count: f.engagement?.share_count,
              subscribe_count: f.engagement?.subscribe_count,
              view_count: f.engagement?.view_count,
              votes_down_count: f.engagement?.votes_down_count,
              votes_up_count: f.engagement?.votes_up_count,
              engagement_rate: f.engagement?.rate,
              total_engagement: f.engagement?.total,
              impression: f.impression,
            };
            return mention;
          }) || [],
      ) || []
    );
  }, [data?.feedbacks]);

  const onExportFeedbackListClick = async (
    reason?: string,
    password?: string,
  ) => {
    await service.heySyncly.ExportMentionList({
      chat_alias: props.options.chat_alias,
      message_id: props.options.message_id,
      citation_ids: props.options.citation_ids,
      reason,
      password,
    });
  };

  return (
    <ModalStyle status={true} pos={props.pos} ref={modalRef}>
      <Wrapper f={1}>
        <Flex j="end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            <FeedbackExport onExportClick={onExportFeedbackListClick}>
              <IconButton
                icon={<DownloadOutlined />}
                tooltip="Export to CSV"
                allowPropagation
              />
            </FeedbackExport>
            <IconButton
              icon={<CloseOutlined />}
              onClick={onCloseClick}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>
        <div className="px-8 py-2 flex justify-between items-center">
          <h3 className="text-gray-800 text-base font-semibold">
            {commaNumber(flattenMentionItems.length || 0)} mentions
          </h3>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </div>
        <List
          isLoading={isLoading}
          data={flattenMentionItems || []}
          className="gap-1 px-5 pb-6 h-full"
          render={(mention) => (
            <MentionItem
              key={mention.alias}
              mention={mention}
              onClick={() => {
                onFeedbackItemClick({
                  feedbackAlias: mention.feedback_alias,
                  blockAlias: mention.alias,
                });
              }}
            />
          )}
        />
      </Wrapper>
    </ModalStyle>
  );
};

const Wrapper = styled(VFlex)`
  overflow: auto;
  height: 100%;
`;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;
